import React from "react";
import { commaJoin } from "shared-utils";
import _compact from "lodash/compact";
import { useTranslation } from "i18n";
import ProfileCard from "pages/Profile/components/common/ProfileCard";
import { useGetBasicInfo } from "pages/Profile/hooks/useGetBasicInfo";
import ExpandableText from "pages/Profile/components/common/ExpandableText";
import { BASIC_DETAILS_FULL_PATH } from "shared/constants";
import cx from "classnames";
import { dataTestIds } from "data-testids";

const TEXT_EXPANDABLE_LIMIT = 250;
const TEXT_SHOW_MORE_LIMIT = 50;

const sectionTextBaseClass = "break-words dark:text-white/70";

interface Props {
  isEdit?: boolean;
}

const BasicInfoSection = ({ isEdit }: Props) => {
  const { t } = useTranslation();
  const { basicInfo, isLoading } = useGetBasicInfo();

  const specs = basicInfo
    ? [
        {
          label: t("common:label.yourLocation"),
          value: commaJoin(basicInfo.locations, ",\n"),
          classNames: "whitespace-pre-wrap",
        },
        {
          label: t("common:label.workPermits"),
          value: commaJoin(basicInfo.visasAndPermits),
        },
        {
          label: t("common:label.gender"),
          value: commaJoin(basicInfo.genders),
        },
        {
          label: t("common:label.disability"),
          value: commaJoin(basicInfo.disabilities),
        },
        { label: t("common:label.actualAge"), value: basicInfo.actualAge },
        { label: t("common:label.playingAge"), value: basicInfo.playingAge },
        { label: t("common:label.height"), value: basicInfo.height },
        {
          label: t("common:label.nationalities"),
          value: commaJoin(basicInfo.nationalities),
        },
        {
          label: t("common:label.ethnicityOrHeritage"),
          value: commaJoin(basicInfo.ethnicity),
        },
        {
          label: t("common:label.membership"),
          value: commaJoin(basicInfo.memberships),
        },
        {
          label: t("common:label.appearance"),
          value: commaJoin(basicInfo.appearances),
        },
        { label: t("common:label.complexionShades"), value: basicInfo.makeUp },
        { label: t("common:label.twin"), value: basicInfo.hasTwin },
        { label: t("common:label.hairColour"), value: basicInfo.hairColor },
        { label: t("common:label.hairLength"), value: basicInfo.hairLength },
        { label: t("common:label.facialHair"), value: basicInfo.facialHair },
        { label: t("common:label.eyeColour"), value: basicInfo.eyeColor },
        {
          label: t("common:label.voiceStyles"),
          value: commaJoin(basicInfo.voiceStyle),
        },
        { label: t("common:label.vocalRange"), value: basicInfo.vocalRange },
        { label: t("common:label.vocalAge"), value: basicInfo.vocalAge },
        { label: t("common:label.hat"), value: basicInfo.hatSize },
        { label: t("common:label.chest"), value: basicInfo.chestSize },
        { label: t("common:label.waist"), value: basicInfo.waistSize },
        { label: t("common:label.hips"), value: basicInfo.hipsSize },
        { label: t("common:label.insideArm"), value: basicInfo.insideArmSize },
        { label: t("common:label.insideLeg"), value: basicInfo.insideLegSize },
        { label: t("common:label.weight"), value: basicInfo.weight },
        { label: t("common:label.collar"), value: basicInfo.collarSize },
        { label: t("common:label.shoeSize"), value: basicInfo.shoeSize },
        { label: t("common:label.ukDressSize"), value: basicInfo.dressSize },
      ]
    : [];

  return (
    <ProfileCard
      className="grid grid-cols-1 gap-1.5 text-s"
      isEdit={isEdit}
      isLoading={isLoading}
      editToPath={BASIC_DETAILS_FULL_PATH}
      data-testid={dataTestIds.profile["Profile.ProfileCard.basicInfoSection"]}
    >
      {_compact(
        specs.map((item) => {
          if (!item.value) return null;

          return (
            <div
              key={item.label}
              className="flex flex-wrap gap-1.5"
              data-testid={`${dataTestIds.profile["Profile.BaicInfoSection.itemDetail"]}-${item.label}`}
            >
              <span className="shrink-0 font-semibold dark:text-white/90">
                {item.label}:
              </span>
              {item.value.length >= TEXT_EXPANDABLE_LIMIT ? (
                <ExpandableText
                  text={item.value}
                  limit={TEXT_SHOW_MORE_LIMIT}
                  textClassName={cx(
                    "text-s",
                    sectionTextBaseClass,
                    item.classNames
                  )}
                />
              ) : (
                <span className={cx(sectionTextBaseClass, item.classNames)}>
                  {item.value}
                </span>
              )}
            </div>
          );
        })
      )}
    </ProfileCard>
  );
};

export default BasicInfoSection;
